import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Plus, Edit, Trash2, X, Save } from 'lucide-react'
import { apiFetch } from '../../config/api'

interface User {
  id: number
  email: string
  last_login: string | null
  created_at: string
}

export default function UsersManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({ email: '', password: '' })

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await apiFetch('/users', {
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setUsers(data.users)
        setError('')
      } else {
        setError(data.error || 'Fehler beim Laden der Benutzer')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Fetch users error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email || !formData.password) {
      setError('Bitte füllen Sie alle Felder aus.')
      return
    }

    try {
      const response = await apiFetch('/users', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setShowAddForm(false)
        setFormData({ email: '', password: '' })
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Erstellen des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add user error:', err)
    }
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.email) {
      setError('E-Mail ist erforderlich.')
      return
    }

    if (!editingUser) return

    try {
      const response = await apiFetch('/users', {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          id: editingUser.id,
          email: formData.email,
          password: formData.password || undefined,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEditingUser(null)
        setFormData({ email: '', password: '' })
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Aktualisieren des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update user error:', err)
    }
  }

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      return
    }

    try {
      const response = await apiFetch(`/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        fetchUsers()
      } else {
        setError(data.error || 'Fehler beim Löschen des Benutzers')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Delete user error:', err)
    }
  }

  const startEdit = (user: User) => {
    setEditingUser(user)
    setFormData({ email: user.email, password: '' })
    setShowAddForm(false)
  }

  const cancelEdit = () => {
    setEditingUser(null)
    setFormData({ email: '', password: '' })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie'
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">
            Benutzerverwaltung
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Zugänge für das Dashboard.</p>
        </div>
        {!showAddForm && !editingUser && (
          <motion.button
            onClick={() => {
              setShowAddForm(true)
              setEditingUser(null)
              setFormData({ email: '', password: '' })
            }}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-900 px-3 text-sm font-medium text-white shadow-sm hover:bg-zinc-800"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="h-5 w-5" />
            Neuer Benutzer
          </motion.button>
        )}
      </div>

      {error && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingUser) && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 relative bg-white rounded-xl shadow-sm border border-zinc-200 p-6 overflow-hidden"
        >
          {/* Subtle gradient overlay */}
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-zinc-950 mb-2">
                  {editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
                </h2>
              </div>
              <motion.button
                onClick={() => {
                  setShowAddForm(false)
                  cancelEdit()
                }}
                className="inline-flex size-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="h-6 w-6" />
              </motion.button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-zinc-600 mb-2">
                  E-Mail
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-dashboard"
                  placeholder="benutzer@email.de"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-600 mb-2">
                  Passwort {editingUser && <span className="text-zinc-500">(leer lassen, um nicht zu ändern)</span>}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-dashboard"
                  placeholder="••••••••"
                  required={!editingUser}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <motion.button
                  type="submit"
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-zinc-900 px-3 text-sm font-medium text-white shadow-sm hover:bg-zinc-800"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="h-5 w-5" />
                  {editingUser ? 'Aktualisieren' : 'Erstellen'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    cancelEdit()
                  }}
                  className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Abbrechen
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden"
      >
        {/* Subtle gradient overlay */}
        
        <div className="relative z-10">
          {loading ? (
            <div className="p-12 text-center text-zinc-500">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Users className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Benutzer...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-600">Keine Benutzer gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-zinc-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-zinc-500">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-zinc-500">
                      E-Mail
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-zinc-500">
                      Erstellt am
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-zinc-500">
                      Letzter Login
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-zinc-500">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {users.map((user, index) => (
                    <motion.tr 
                      key={user.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-zinc-50 transition-colors group"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                        {user.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-950 font-medium">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-500">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <motion.button
                            onClick={() => startEdit(user)}
                            className="text-zinc-700 hover:text-zinc-950"
                            title="Bearbeiten"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="h-5 w-5" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Löschen"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="h-5 w-5" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
